import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import TokenPurchaseModal from '../components/TokenPurchaseModal';

interface SelectionPosition {
  top: number;
  left: number;
}

interface UseTextSelectionAIOptions {
  enabled?: boolean;
  onUpdate?: (newText: string) => void;
}

export function useTextSelectionAI(options: UseTextSelectionAIOptions = {}) {
  const { enabled = true, onUpdate } = options;
  const { selectedBusiness } = useBusiness();
  
  const [showAIButton, setShowAIButton] = useState(false);
  const [showPromptInput, setShowPromptInput] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionPosition, setSelectionPosition] = useState<SelectionPosition>({ top: 0, left: 0 });
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedText, setGeneratedText] = useState<string | null>(null);
  const [activeElement, setActiveElement] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [promptPosition, setPromptPosition] = useState<{ top: number; left: string; transform: string }>({ 
    top: 0, 
    left: '50%', 
    transform: 'translateX(-50%)' 
  });
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
  const isInteractingWithAI = useRef(false);
  const savedRangeRef = useRef<Range | null>(null);
  const contentEditableRef = useRef<HTMLElement | null>(null);
  const [isContentEditable, setIsContentEditable] = useState(false);
  const selectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Find the closest contentEditable ancestor of a node
  const findContentEditable = useCallback((node: Node | null): HTMLElement | null => {
    if (!node) return null;
    let el: HTMLElement | null = node instanceof HTMLElement ? node : node.parentElement;
    while (el) {
      // Check the contenteditable attribute directly (most reliable)
      const attr = el.getAttribute('contenteditable');
      if (attr === 'true' || attr === '') return el;
      // Also check the DOM property
      if (el.contentEditable === 'true') return el;
      el = el.parentElement;
    }
    return null;
  }, []);

  // Handle text selection globally
  const handleSelection = useCallback(() => {
    if (!enabled) return;

    // 1) Check window.getSelection() for any non-collapsed selection (works for contentEditable)
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      const selected = sel.toString().trim();
      if (selected.length > 0) {
        const anchorNode = sel.anchorNode;
        const ceEl = findContentEditable(anchorNode);
        
        if (ceEl) {
          const range = sel.getRangeAt(0);
          savedRangeRef.current = range.cloneRange();
          contentEditableRef.current = ceEl;
          setIsContentEditable(true);
          setSelectedText(selected);
          setActiveElement(null);

          const rect = range.getBoundingClientRect();
          const top = Math.max(8, rect.top - 42);
          const left = Math.max(8, rect.left);
          setSelectionPosition({ top, left });

          setShowAIButton(true);
          setShowPromptInput(false);
          setPrompt('');
          return;
        }
      }
    }
    
    // 2) Check input/textarea selections
    const activeEl = document.activeElement as HTMLInputElement | HTMLTextAreaElement;
    if (
      activeEl &&
      (activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'INPUT') &&
      activeEl.selectionStart !== null &&
      activeEl.selectionEnd !== null &&
      activeEl.selectionStart !== activeEl.selectionEnd
    ) {
      const start = activeEl.selectionStart;
      const end = activeEl.selectionEnd;
      const selected = activeEl.value.substring(start, end).trim();
      
      if (selected.length > 0) {
        setIsContentEditable(false);
        savedRangeRef.current = null;
        contentEditableRef.current = null;
        setSelectedText(selected);
        setActiveElement(activeEl);
        setSelectionStart(start);
        setSelectionEnd(end);
        
        const elementRect = activeEl.getBoundingClientRect();
        const isMobile = window.innerWidth < 768;
        const buttonTop = isMobile ? elementRect.top - 40 : elementRect.top;
        
        setSelectionPosition({
          top: buttonTop + window.scrollY,
          left: elementRect.left + window.scrollX
        });
        
        setShowAIButton(true);
        setShowPromptInput(false);
        setPrompt('');
        return;
      }
    }
    
    // Hide button if no valid selection AND not interacting with AI UI
    if (!showPromptInput && !isInteractingWithAI.current) {
      setShowAIButton(false);
    }
  }, [enabled, showPromptInput, findContentEditable]);

  // Handle AI prompt submission
  const handlePromptSubmit = useCallback(async () => {
    if (!prompt.trim()) return;
    if (!activeElement && !contentEditableRef.current) return;

    // Use generatedText if it exists (for iterative editing), otherwise use selectedText
    const textToEdit = generatedText || selectedText;
    if (!textToEdit) return;

    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-edit-text', {
        body: {
          prompt: prompt.trim(),
          selectedText: textToEdit,
          businessId: selectedBusiness?.id
        }
      });

      // Check for insufficient tokens FIRST (even if there's an error)
      if (data?.insufficientTokens) {
        setShowTokenPurchaseModal(true);
        setIsProcessing(false);
        return;
      }

      if (error) throw error;

      if (data?.success && data.text) {
        // Strip any leading/trailing quotes that AI might add
        let cleanedText = data.text.trim();
        if ((cleanedText.startsWith('"') && cleanedText.endsWith('"')) ||
            (cleanedText.startsWith("'") && cleanedText.endsWith("'"))) {
          cleanedText = cleanedText.slice(1, -1);
        }
        // Show the generated text for preview instead of auto-applying
        setGeneratedText(cleanedText);
        setPrompt(''); // Clear the prompt for next iteration
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error processing selection:', error);
      setIsProcessing(false);
    }
  }, [prompt, selectedText, generatedText, activeElement, isContentEditable, selectedBusiness?.id]);

  // Handle applying the generated text
  const handleApplyText = useCallback(() => {
    if (!generatedText) return;

    if (isContentEditable && savedRangeRef.current && contentEditableRef.current) {
      const el = contentEditableRef.current;
      el.focus();
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
        document.execCommand('insertText', false, generatedText);
      }
      const inputEvt = new Event('input', { bubbles: true });
      el.dispatchEvent(inputEvt);
      savedRangeRef.current = null;
      contentEditableRef.current = null;
    } else if (activeElement) {
      const currentValue = activeElement.value;
      const newValue = 
        currentValue.substring(0, selectionStart) + 
        generatedText + 
        currentValue.substring(selectionEnd);
      
      activeElement.value = newValue;
      const newCursorPos = selectionStart + generatedText.length;
      activeElement.setSelectionRange(newCursorPos, newCursorPos);
      activeElement.focus();
      
      const event = new Event('input', { bubbles: true });
      activeElement.dispatchEvent(event);
      
      if (onUpdate) {
        onUpdate(newValue);
      }
    }
    
    // Reset state
    setShowAIButton(false);
    setShowPromptInput(false);
    setPrompt('');
    setSelectedText('');
    setGeneratedText(null);
    isInteractingWithAI.current = false;
  }, [generatedText, activeElement, selectionStart, selectionEnd, onUpdate, isContentEditable]);

  // Maintain selection visually by re-applying it periodically
  useEffect(() => {
    if (!showPromptInput) return;
    if (!activeElement && !contentEditableRef.current) return;

    const interval = setInterval(() => {
      if (isContentEditable && savedRangeRef.current && contentEditableRef.current) {
        // For contentEditable, no need to constantly re-apply — just keep the ref
      } else if (activeElement && document.activeElement !== activeElement) {
        const currentFocus = document.activeElement;
        activeElement.setSelectionRange(selectionStart, selectionEnd);
        if (currentFocus instanceof HTMLElement) {
          currentFocus.focus();
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [showPromptInput, activeElement, selectionStart, selectionEnd, isContentEditable]);

  // Close on click outside
  useEffect(() => {
    if (!showAIButton && !showPromptInput) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-selection-ai-global]')) {
        isInteractingWithAI.current = false;
        setShowAIButton(false);
        setShowPromptInput(false);
        setPrompt('');
        setGeneratedText(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAIButton, showPromptInput]);

  // Listen for text selection globally
  useEffect(() => {
    if (!enabled) return;

    const handlePointerEnd = () => {
      requestAnimationFrame(() => {
        if (isInteractingWithAI.current) return;
        handleSelection();
      });
    };

    const handleKeySelection = () => {
      if (selectionTimerRef.current) clearTimeout(selectionTimerRef.current);
      selectionTimerRef.current = setTimeout(() => {
        if (isInteractingWithAI.current) return;
        handleSelection();
      }, 150);
    };

    // Custom event from contentEditable InlineEdit components
    const handleCESelection = () => {
      if (isInteractingWithAI.current) return;
      handleSelection();
    };

    // Capture phase for mouseup/touchend, plus custom event for contentEditable
    document.addEventListener('mouseup', handlePointerEnd, true);
    document.addEventListener('touchend', handlePointerEnd, true);
    document.addEventListener('keyup', handleKeySelection, true);
    document.addEventListener('ce-text-selected', handleCESelection);

    return () => {
      document.removeEventListener('mouseup', handlePointerEnd, true);
      document.removeEventListener('touchend', handlePointerEnd, true);
      document.removeEventListener('keyup', handleKeySelection, true);
      document.removeEventListener('ce-text-selected', handleCESelection);
      if (selectionTimerRef.current) clearTimeout(selectionTimerRef.current);
    };
  }, [enabled, handleSelection]);

  // Render the UI
  const renderUI = () => {
    if (!showAIButton && !showPromptInput) return null;

    return createPortal(
      <div>
        {/* AI Button */}
        {showAIButton && !showPromptInput && !isProcessing && (
          <div
            data-selection-ai-global
            style={{
              position: 'fixed',
              top: `${selectionPosition.top}px`,
              left: `${selectionPosition.left}px`,
              zIndex: 9999999
            }}
          >
            <button
              type="button"
               onMouseDown={(e) => {
                 e.preventDefault();
                 e.stopPropagation();
                 isInteractingWithAI.current = true;
                 
                 // Restore selection to keep text highlighted
                 if (isContentEditable && savedRangeRef.current && contentEditableRef.current) {
                   contentEditableRef.current.focus();
                   const sel = window.getSelection();
                   if (sel) {
                     sel.removeAllRanges();
                     sel.addRange(savedRangeRef.current);
                   }
                 } else if (activeElement) {
                   activeElement.focus();
                   activeElement.setSelectionRange(selectionStart, selectionEnd);
                 }
                 
                 // Calculate prompt position with boundary checking
                 const isMobile = window.innerWidth < 768;
                 const promptHeight = 250;
                 const promptWidth = isMobile ? window.innerWidth - 32 : 500;
                 let top = selectionPosition.top + 50;
                 
                 // Check if prompt would go off bottom of screen
                 const viewH = window.innerHeight;
                 if (top + promptHeight > viewH) {
                   top = Math.max(20, selectionPosition.top - promptHeight - 10);
                 }
                 
                 // For mobile, center horizontally with padding
                 let left = '50%';
                 let transform = 'translateX(-50%)';
                 
                 if (isMobile) {
                   // On mobile, ensure it fits within screen width with padding
                   left = '16px';
                   transform = 'none';
                 } else {
                   // Desktop - Check if prompt would go off right side of screen
                   const centerLeft = window.innerWidth / 2;
                   
                   if (centerLeft + promptWidth / 2 > window.innerWidth) {
                     // Too close to right edge
                     left = `${window.innerWidth - promptWidth - 20}px`;
                     transform = 'none';
                   } else if (centerLeft - promptWidth / 2 < 0) {
                     // Too close to left edge
                     left = '20px';
                     transform = 'none';
                   }
                 }
                 
                 setPromptPosition({ top, left, transform });
                 setShowPromptInput(true);
                 setShowAIButton(false);
               }}
              className="w-8 h-8 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
              title="Edit with AI"
            >
              <Sparkles className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Prompt Input */}
        {showPromptInput && (
          <div
            data-selection-ai-global
            style={{
              position: 'fixed',
              top: `${promptPosition.top}px`,
              left: promptPosition.left,
              transform: promptPosition.transform,
              zIndex: 9999999
            }}
          >
            <div className="bg-gray-800 rounded-2xl shadow-2xl px-5 py-3 w-[calc(100vw-32px)] sm:min-w-[420px] sm:max-w-[500px] sm:w-auto">
              {/* Show selected text preview */}
              {!generatedText && !isProcessing && (
                <div className="mb-3 px-3 py-2 bg-blue-500/20 border border-blue-400/30 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wide text-blue-300 mb-1 font-semibold">Editing</div>
                  <div className="text-sm text-white font-mono break-words">
                    "{(() => {
                      const words = selectedText.split(' ');
                      if (words.length > 10) {
                        return words.slice(0, 10).join(' ') + '...';
                      }
                      return selectedText;
                    })()}"
                  </div>
                </div>
              )}
              
              {/* Show processing state */}
              {isProcessing && (
                <div className="mb-3 px-3 py-2 bg-blue-500/20 border border-blue-400/30 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wide text-blue-300 mb-1 font-semibold">Processing</div>
                  <div className="text-sm text-blue-200 font-mono break-words flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </div>
                </div>
              )}
              
              {/* Show generated text preview */}
              {generatedText && !isProcessing && (
                <div className="mb-3 px-3 py-2 bg-blue-500/20 border border-blue-400/30 rounded-lg">
                  <div className="text-[10px] uppercase tracking-wide text-blue-300 mb-1 font-semibold">Preview</div>
                  <div className="text-sm text-white font-mono break-words max-h-32 overflow-y-auto">
                    "{generatedText}"
                  </div>
                </div>
              )}
              
              {/* Input row - always show when not processing */}
              {!isProcessing && (
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-2 text-gray-400">
                    <div className="flex flex-col gap-0.5">
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && prompt.trim()) {
                        e.preventDefault();
                        handlePromptSubmit();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setShowAIButton(false);
                        setShowPromptInput(false);
                        setPrompt('');
                        setGeneratedText(null);
                      }
                    }}
                    placeholder={generatedText ? "Further refine..." : "Describe your change"}
                    className="flex-1 bg-transparent text-white placeholder-gray-400 text-sm focus:outline-none py-1"
                    autoFocus
                  />
                </div>
              )}
              
              {/* Apply button - only show when we have generated text */}
              {generatedText && !isProcessing && (
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setGeneratedText(null);
                      setPrompt('');
                    }}
                    className="w-full sm:flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyText}
                    className="w-full sm:flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Token Purchase Modal */}
        <TokenPurchaseModal
          isOpen={showTokenPurchaseModal}
          onClose={() => setShowTokenPurchaseModal(false)}
          businessId={selectedBusiness?.id || ''}
          onSuccess={() => {
            setShowTokenPurchaseModal(false);
            alert('Tokens added successfully! You can now use AI features.');
          }}
        />
      </div>,
      document.body
    );
  };

  return {
    renderUI,
    isActive: showAIButton || showPromptInput
  };
}

